import NiceModal from "@ebay/nice-modal-react";
import { subMonths } from "date-fns";
import { FormProvider } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import Box from "components/Box";
import Button from "components/Button";
import DateRangeField from "components/DateRangeField";
import Empty from "components/Empty";
import SimpleModal from "components/modals/SimpleModal";
import Pagination from "components/Pagination";
import RawCheckbox from "components/RawCheckbox";
import EnhancedSearchWithTypeField from "components/SearchWithTypeField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useDeleteNotices, useGetNotices } from "hooks/apis/notice";
import useHandleParams from "hooks/useHandleParams";
import useSelection from "hooks/useSelection";
import format from "lib/format";
import { noticeSearchTypeOptions, noticeTypesOptions } from "utils/const";

type FormValues = {
  page: number;
  startDate?: string;
  endDate?: string;
  category?: string;
  searchType?: string;
  searchText?: string;
  sort?: string;
};

const size = 20;

const SettingsNoticePage = () => {
  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      startDate: "",
      endDate: "",
      category: "all",
      searchType: "title",
      searchText: "",
      page: 1,
    },
  });

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetNotices({
    type: "notice",
    size,
    ...form,
  });
  const navigate = useNavigate();
  const deleteNoticesMutation = useDeleteNotices();

  const { onSelectAll, onSelectItem, selection, resetSelection } =
    useSelection<number>(data);

  const writeNotice = () => {
    navigate("/settings/notice/write");
  };

  const onDeleteNotices = () => {
    NiceModal.show(SimpleModal, {
      title: "공지사항 삭제",
      message: "선택한 공지사항을 삭제하시겠습니까?",
      submitText: "삭제",
    })
      .then(() => {
        deleteNoticesMutation.mutate(
          { ids: selection, type: "notice" },
          { onSuccess: resetSelection },
        );
      })
      .catch(() => {});
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title="운영관리"
          description="서비스 운영 관리 화면입니다."
        />
        <Box className="flex justify-between">
          <h3 className="text-18 font-medium">공지사항 관리</h3>
        </Box>
        <Box
          backgroundColor="gray"
          className="py-[0.625rem] px-[1.875rem] flex gap-[3.125rem] 1500:!pr-[6.5rem]"
        >
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">기간 검색</dt>
              <dd>
                <DateRangeField />
              </dd>
            </dl>
          </div>
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">유형</dt>
              <dd>
                <Select name="category" options={noticeTypesOptions} />
              </dd>
              <dt className="flex items-center">검색 상세</dt>
              <dd className="flex gap-[0.313rem]">
                <EnhancedSearchWithTypeField
                  ref={searchRef}
                  options={noticeSearchTypeOptions}
                />
              </dd>
            </dl>
          </div>
        </Box>
        <div className="flex justify-end gap-[0.625rem] mt-[0.875rem] mb-[0.625rem]">
          <Button
            onClick={onDeleteNotices}
            disabled={!selection?.length}
            className="w-[9.375rem]"
            outline
            color="primary"
          >
            {selection?.length ? `삭제 (${selection.length})` : "삭제"}
          </Button>
          <Button
            className="w-[9.375rem]"
            color="primary"
            onClick={writeNotice}
          >
            공지사항 등록
          </Button>
        </div>
        <Box className="!py-5 min-h-[calc(100%-20.188rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={[
              "w-[4.5rem] justify-center",
              "w-[6.25rem]",
              "w-[8.75rem]",
              "flex-1 px-5 text-left",
              "w-[15.375rem]",
              "w-[11.188rem]",
              "w-[8.125rem]",
            ]}
          >
            <thead>
              <tr>
                <th>
                  <RawCheckbox
                    checked={
                      !!selection?.length && selection.length === data?.length
                    }
                    onChange={onSelectAll}
                  />
                </th>
                <th>번호</th>
                <th>유형</th>
                <th>제목</th>
                <th>
                  <SortButton name="created">등록일자</SortButton>
                </th>
                <th>작성자</th>
                <th>수정</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((notice, index) => (
                <tr key={notice.id}>
                  <td>
                    <RawCheckbox
                      checked={selection.includes(notice.id)}
                      onChange={() => {
                        onSelectItem(notice.id);
                      }}
                    />
                  </td>
                  <td>{totalItems - index - size * (currentPage - 1)}</td>
                  <td>{notice.type}</td>
                  <td>
                    <p className="line-clamp-1 break-all">{notice.title}</p>
                  </td>
                  <td>{format(notice.createdAt, "yyyy/MM/dd HH:mm:ss")}</td>
                  <td>{notice.author}</td>
                  <td>
                    <Link
                      to={`/settings/notice/${notice.id}/edit`}
                      type="button"
                      className="mx-auto w-[3.125rem] h-[1.625rem] inline-flex justify-center items-center rounded-md bg-gray"
                    >
                      수정
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          <Pagination total={totalItems} />
        </Box>
      </main>
    </FormProvider>
  );
};

export default SettingsNoticePage;
